import type { ZodTypeAny } from "zod";
import type { DocumentSnapshot, QuerySnapshot } from "firebase/firestore";
import { captureInvalidType } from "./reporting";
import type { Options } from "./types";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const processDocument =
  <TKeyPath extends string | undefined>(
    model: ZodTypeAny,
    options: Options<TKeyPath>,
    path: string,
  ) =>
  (snapshot: DocumentSnapshot): unknown => {
    if (!snapshot.exists()) {
      return null;
    }

    const docData = snapshot.data();
    const parsed = model.safeParse(docData);
    // zod v4 types parse output as unknown; the guard narrows it back to an
    // indexable record, preserving the legacy raw-data fallback on failure
    const parsedData =
      parsed.success && isRecord(parsed.data) ? parsed.data : undefined;
    const data = parsedData ?? docData ?? {};

    // extend the data object to include the document ID
    if (options.keyPath) {
      data[options.keyPath] = snapshot.id;
    }

    if (!parsed.success) {
      // capture failed data for debugging
      captureInvalidType(data, parsed.error, path);
      console.error(
        `Invalid data received from firestore. Path: ${path}`,
        data,
        parsed.error,
      );
    }

    return data;
  };

export const processCollection =
  <TKeyPath extends string | undefined>(
    model: ZodTypeAny,
    options: Options<TKeyPath>,
    path: string,
  ) =>
  (snapshot: QuerySnapshot): unknown => {
    if (snapshot.size === 0) {
      // if node is empty return null result
      return null;
    }

    const docsData = snapshot.docs.map((document) => document.data());
    const parsed = model.safeParse(docsData);

    const parsedData =
      parsed.success && Array.isArray(parsed.data) ? parsed.data : undefined;
    let data: unknown[] = parsedData ?? docsData;

    if (options.keyPath) {
      const key = options.keyPath;
      data = data.map((item, index) => {
        const document = snapshot.docs[index];
        return {
          ...(isRecord(item) ? item : {}),
          [key]: document.id,
        };
      });
    }

    if (!parsed.success) {
      // capture failed data for debugging
      captureInvalidType(data, parsed.error, path);
      console.error(
        `Invalid data received from firestore. Path: ${path}`,
        data,
        parsed.error.issues,
      );
    }

    return data;
  };
